import {
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Image,
  Text,
} from '@chakra-ui/react';
import { ReactNode, useCallback, useRef, useState } from 'react';
import Dropzone, { DropzoneProps, DropzoneRef } from 'react-dropzone';
import { FaUserCircle } from 'react-icons/fa';
import { RiCameraLine, RiDeleteBinLine } from 'react-icons/ri';
import { WebcamModal } from './components/WebcamModal';

interface IAvatarInputProps extends DropzoneProps {
  avatarUrl?: string;
  onChange: (file: File) => void;
  onDelete: () => void;
  showWebcamButton?: boolean;
}

export const AvatarInput = ({
  avatarUrl,
  onChange,
  onDelete,
  showWebcamButton = false,
  disabled,
  ...rest
}: IAvatarInputProps): JSX.Element => {
  const dropZoneRef = useRef<DropzoneRef>(null);

  const [isWebcamModalVisible, setIsWebcamModalVisible] = useState(false);
  const [isDropRejected, setIsDropRejected] = useState(false);

  const toggleWebcamModal = (): void => {
    setIsWebcamModalVisible((prevState) => !prevState);
  };

  const handleFileSelect = (file: File): void => {
    setIsDropRejected(false);

    onChange(file);
  };

  const handleWebcamCapture = (file: File): void => {
    if (file.size > 1000000) {
      setIsDropRejected(true);

      return;
    }

    onChange(file);

    toggleWebcamModal();
  };

  const renderDragMessage = useCallback(
    (isDragActive: boolean, isDragReject: boolean): ReactNode => {
      if (!isDragActive) {
        return (
          <Flex mt="2" justify="center" align="center" direction="column">
            {isDragReject && (
              <Text fontSize="12" color="red.500">
                Arquivo não suportado
              </Text>
            )}

            <Text mt="2" fontSize="12" color="gray.500">
              Clique para selecionar ou arraste e solte uma imagem aqui
            </Text>

            <Text fontSize="12" color="gray.500">
              (máx. 1MB)
            </Text>
          </Flex>
        );
      }

      return (
        <Flex h="12" mt="4" justify="center" align="center">
          <Text fontSize="12" color="green.500">
            Solte o arquivo aqui
          </Text>
        </Flex>
      );
    },
    [],
  );

  return (
    <Box position="relative">
      <WebcamModal
        isOpen={isWebcamModalVisible}
        onClose={toggleWebcamModal}
        onCapture={handleWebcamCapture}
      />

      <Dropzone
        ref={dropZoneRef}
        accept="image/*"
        disabled={disabled}
        maxSize={1000000}
        onDropRejected={() => {
          setIsDropRejected(true);
        }}
        onDropAccepted={(files) => handleFileSelect(files[0])}
        {...rest}
      >
        {({ getRootProps, getInputProps, isDragActive }) => {
          let currentBorderColor = 'gray.300';
          let currentBgColor = 'gray.100';
          let currentIconColor = 'gray.400';

          if (isDropRejected) {
            currentBorderColor = 'red.300';
            currentIconColor = 'red.400';
            currentBgColor = 'red.50';
          }

          if (isDragActive) {
            currentBorderColor = 'green.300';
            currentIconColor = 'green.400';
            currentBgColor = 'green.50';
          }

          return (
            <Box
              h="60"
              w="40"
              p={avatarUrl && !isDropRejected ? '0' : '4'}
              bg={currentBgColor}
              borderColor={currentBorderColor}
              borderWidth={1}
              borderRadius={8}
              borderBottomLeftRadius={showWebcamButton ? 0 : 8}
              borderBottomRightRadius={showWebcamButton ? 0 : 8}
              cursor="pointer"
              textAlign="center"
              className="dropzone"
              {...getRootProps()}
            >
              {(!avatarUrl || isDropRejected) && (
                <Box>
                  <Icon
                    fontSize="60"
                    color={currentIconColor}
                    as={FaUserCircle}
                  />
                  {renderDragMessage(isDragActive, isDropRejected)}
                </Box>
              )}

              {avatarUrl && !isDropRejected && (
                <Image
                  h="100%"
                  w="100%"
                  borderRadius={8}
                  borderBottomLeftRadius={showWebcamButton ? 0 : 8}
                  borderBottomRightRadius={showWebcamButton ? 0 : 8}
                  src={avatarUrl}
                  alt="avatar"
                />
              )}

              <input {...getInputProps()} />
            </Box>
          );
        }}
      </Dropzone>

      {showWebcamButton && (
        <IconButton
          aria-label="webcam"
          icon={<Icon as={RiCameraLine} />}
          fontSize="24"
          width="100%"
          color="gray.500"
          colorScheme="gray"
          borderColor="gray.300"
          borderWidth={1}
          borderRadius={8}
          borderTopLeftRadius={0}
          borderTopRightRadius={0}
          onClick={toggleWebcamModal}
        />
      )}

      {avatarUrl && !isDropRejected && !disabled && (
        <Button
          p={0}
          size="sm"
          borderRadius="50%"
          colorScheme="red"
          onClick={onDelete}
          justifyContent="center"
          alignItems="center"
          position="absolute"
          top="-4"
          right="-4"
        >
          <Icon as={RiDeleteBinLine} fontSize="16" />
        </Button>
      )}
    </Box>
  );
};
